import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import isPermissionEnabled from "common/isPermissionEnabled"
import { tenantGetMyIntegrations } from "store/actions/tenantIntegrationAction"
import {
  normalizePosIntegration,
  shouldShowLegacyPosApiKeyTab,
} from "utils/posBranchSyncUtils"
import {
  getTenantPosConnectionsAttemptKey,
  resetTenantPosConnectionsAttempt,
  resolveTenantPosAttemptKey,
  setTenantPosConnectionsAttemptKey,
} from "utils/tenantPosConnectionsAttempt"

export { resetTenantPosConnectionsAttempt }

const canViewTenantIntegrations = (userRole, accountType, userIntegrations) => {
  if (
    isPermissionEnabled(userRole, 48, "tenant_integrations_1", accountType)
  ) {
    return true
  }
  if (
    Array.isArray(userIntegrations) &&
    userIntegrations.some(
      int => int?.permissions?.tenant_integrations_1 === true
    )
  ) {
    return true
  }
  const map = userRole?.integrationPermissions
  if (!map || typeof map !== "object" || Array.isArray(map)) {
    return false
  }
  return Object.values(map).some(
    entry =>
      entry &&
      typeof entry === "object" &&
      !Array.isArray(entry) &&
      entry.tenant_integrations_1 === true
  )
}

/**
 * Tenant-level POS integration connection (Settings → Integrations).
 * Used to gate manual push and show connected / not connected on orders.
 */
export const useTenantPosConnections = () => {
  const dispatch = useDispatch()
  const my = useSelector(state => state.TenantIntegration?.my || [])
  const myLoading = useSelector(state => state.TenantIntegration?.myLoading)
  const myTotalCount = useSelector(state => state.TenantIntegration?.myTotalCount)
  const userId = useSelector(state => state.Login?.userId)
  const authUser = useSelector(state => state.Login?.authUser)
  const userRole = useSelector(state => state.Login?.userRole)
  const accountType = useSelector(state => state.Login?.accountType)
  const userIntegrations = useSelector(state => state.Login?.userIntegrations)
  const canViewIntegrations = canViewTenantIntegrations(
    userRole,
    accountType,
    userIntegrations
  )
  const attemptKey = resolveTenantPosAttemptKey(userId, authUser)

  useEffect(() => {
    if (!canViewIntegrations || myLoading) {
      return
    }
    // Must match empty results too — previous guard required truthy userId and
    // Login.userId was never set, so empty /my kept refetching forever.
    if (getTenantPosConnectionsAttemptKey() === attemptKey) {
      return
    }
    if (my.length > 0) {
      setTenantPosConnectionsAttemptKey(attemptKey)
      return
    }
    setTenantPosConnectionsAttemptKey(attemptKey)
    dispatch(
      tenantGetMyIntegrations({
        page: 1,
        perPage: 100,
      })
    )
  }, [attemptKey, canViewIntegrations, dispatch, my.length, myLoading])

  const byCode = useMemo(() => {
    const map = {}
    ;(my || []).forEach(row => {
      const code = normalizePosIntegration(row?.integrationCode || row?.code)
      if (code) {
        map[code] = row
      }
    })
    return map
  }, [my])

  const integrationsLoaded =
    getTenantPosConnectionsAttemptKey() === attemptKey ||
    my.length > 0 ||
    (!myLoading && myTotalCount === 0)

  return {
    byCode,
    loading: myLoading,
    loaded: integrationsLoaded,
    /** False when Wulooj POS (or Foodics) is connected — use Integrations instead. */
    showLegacyPosApiKeyTab: integrationsLoaded
      ? shouldShowLegacyPosApiKeyTab(byCode)
      : true,
  }
}

export default useTenantPosConnections
